import React, { useState, useEffect, useRef } from "react";
import { Modal, Box } from "@mui/material";
import {
  X,
  Tag,
  Search,
  Info,
  FileText,
  Type,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
} from "lucide-react";

export const TagSelectionModal = ({
  open,
  onClose,
  suggestedTags = {},
  tagNotFoundRates = {},
  onConfirm,
  isHierarchical = false,
}) => {
  const [selectedTags, setSelectedTags] = useState({});
  const [selectedTagNames, setSelectedTagNames] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [flatTagsList, setFlatTagsList] = useState([]);
  const [scoreFilter, setScoreFilter] = useState("all"); // "all", "high", "medium", "low", "unscored"
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  const [viewMode, setViewMode] = useState(isHierarchical ? "tree" : "flat"); // "tree" or "flat"
  const [reorganizedTags, setReorganizedTags] = useState({}); // For moving groups around
  const [draggedItem, setDraggedItem] = useState(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (open && suggestedTags) {
      const initialSelected = {};
      const extractedFlatTags = [];

      const processTags = (tags, path = []) => {
        if (!tags || typeof tags !== "object") return;

        Object.keys(tags).forEach((tagName) => {
          const currentPath = [...path, tagName];
          const tagKey = currentPath.join(".");
          initialSelected[tagKey] = false;

          const isTag = tags[tagName]?.TagAvailableValues !== undefined;
          if (isTag) {
            extractedFlatTags.push({
              name: tagName,
              path: currentPath,
              key: tagKey,
              hasChildren:
                typeof tags[tagName] === "object" &&
                !tags[tagName].TagAvailableValues,
              availableValues: Array.isArray(tags[tagName]?.TagAvailableValues)
                ? tags[tagName]?.TagAvailableValues
                : [],
              description: tags[tagName]?.description || "",
              outputType: tags[tagName]?.output_type || "string",
              maxValues: tags[tagName]?.maxValues || "Unlimited",
            });
          }

          if (typeof tags[tagName] === "object")
            processTags(tags[tagName], currentPath);
        });
      };
      processTags(suggestedTags);
      setSelectedTags(initialSelected);
      setSelectedTagNames([]);
      setFlatTagsList(extractedFlatTags);
      setReorganizedTags(JSON.parse(JSON.stringify(suggestedTags))); // Deep copy for reorganization

      // Set appropriate view mode based on hierarchical flag
      setViewMode(isHierarchical ? "tree" : "flat");

      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open, suggestedTags, isHierarchical]);

  // Update selectedTagNames whenever selectedTags changes
  useEffect(() => {
    const tagNames = [];
    Object.entries(selectedTags).forEach(([key, isSelected]) => {
      if (isSelected) {
        const tag = flatTagsList.find((t) => t.key === key);
        if (tag) {
          tagNames.push(tag.name);
        }
      }
    });
    setSelectedTagNames(tagNames);
  }, [selectedTags, flatTagsList]);

  const handleToggleTag = (tagKey, path) => {
    const newSelected = { ...selectedTags };
    const isSelecting = !selectedTags[tagKey];

    newSelected[tagKey] = isSelecting;

    if (isSelecting) {
      if (path.length > 1) {
        for (let i = 1; i < path.length; i++) {
          const parentPath = path.slice(0, i).join(".");
          newSelected[parentPath] = true;
        }
      }

      // Find and select all child tags that start with this path
      const currentTagPathPrefix = path.join(".");

      // Then look through all flat tags to find children
      flatTagsList.forEach((tag) => {
        const otherTagPath = tag.path.join(".");

        // If this tag is a child of the selected tag (starts with the path but is not the same tag)
        if (otherTagPath.startsWith(currentTagPathPrefix + ".")) {
          newSelected[tag.key] = true;
        }
      });
    } else {
      flatTagsList.forEach((tag) => {
        const otherTagPath = tag.path.join(".");
        const currentTagPathPrefix = path.join(".");

        if (otherTagPath.startsWith(currentTagPathPrefix + ".")) {
          newSelected[tag.key] = false;
        }
      });
    }

    setSelectedTags(newSelected);
  };

  const handleSelectAll = () => {
    const newSelected = { ...selectedTags };
    if (scoreFilter === "all") {
      // Select all tags
      Object.keys(selectedTags).forEach((key) => {
        if (key.split(".").length % 2 !== 0) {
          newSelected[key] = true;
        }
      });
    } else {
      // Only select tags that match the current filter
      flatTagsList.forEach((tag) => {
        const score = getScoreFromNotFoundRate(tag.name);
        const category = getScoreCategory(score);
        if (category === scoreFilter) {
          newSelected[tag.key] = true;
        }
      });
    }

    setSelectedTags(newSelected);
  };

  const handleSelectNone = () => {
    const newSelected = { ...selectedTags };

    if (scoreFilter === "all") {
      // Clear all tags
      Object.keys(selectedTags).forEach((key) => {
        newSelected[key] = false;
      });
    } else {
      // Only clear tags that match the current filter
      flatTagsList.forEach((tag) => {
        const score = getScoreFromNotFoundRate(tag.name);
        const category = getScoreCategory(score);
        if (category === scoreFilter) {
          newSelected[tag.key] = false;
        }
      });
    }

    setSelectedTags(newSelected);
  };

  const handleExpandAll = () => {
    setCollapsedNodes(new Set());
  };

  const handleCollapseAll = () => {
    const allParentNodes = new Set();

    // Find all nodes that have children (parent nodes)
    flatTagsList.forEach((tag) => {
      // Check all possible parent paths
      for (let i = 1; i < tag.path.length; i++) {
        const parentPath = tag.path.slice(0, i).join(".");
        allParentNodes.add(parentPath);
      }
    });

    setCollapsedNodes(allParentNodes);
  };

  const handleReset = () => {
    // Reset reorganized tags back to original structure
    setReorganizedTags(JSON.parse(JSON.stringify(suggestedTags)));

    // Clear all selections
    const resetSelected = {};
    Object.keys(selectedTags).forEach((key) => {
      resetSelected[key] = false;
    });
    setSelectedTags(resetSelected);
    setSelectedTagNames([]);

    // Reset collapsed state
    setCollapsedNodes(new Set());

    // Clear drag state
    setDraggedItem(null);

    // Clear search and filters
    setSearchQuery("");
    setScoreFilter("all");
  };

  // Get maximum depth of the hierarchy
  const getMaxDepth = () => {
    const tagsToAnalyze = isHierarchical ? reorganizedTags : suggestedTags;
    let maxDepth = 0;

    const findMaxDepth = (obj, currentDepth = 0) => {
      if (!obj || typeof obj !== "object") return currentDepth;

      const tagMetadataKeys = [
        "TagAvailableValues",
        "description",
        "output_type",
        "maxValues",
      ];
      const childKeys = Object.keys(obj).filter(
        (key) => !tagMetadataKeys.includes(key),
      );

      if (childKeys.length === 0) return currentDepth;

      let deepestChild = currentDepth;
      childKeys.forEach((key) => {
        const childDepth = findMaxDepth(obj[key], currentDepth + 1);
        deepestChild = Math.max(deepestChild, childDepth);
      });

      return deepestChild;
    };

    Object.values(tagsToAnalyze).forEach((rootValue) => {
      const depth = findMaxDepth(rootValue, 1);
      maxDepth = Math.max(maxDepth, depth);
    });

    return maxDepth;
  };

  // Expand all nodes at a specific level
  const handleExpandLevel = (targetLevel) => {
    const newCollapsed = new Set(collapsedNodes);
    const tagsToAnalyze = isHierarchical ? reorganizedTags : suggestedTags;

    const processLevel = (obj, currentPath = [], currentLevel = 1) => {
      if (!obj || typeof obj !== "object") return;

      const tagMetadataKeys = [
        "TagAvailableValues",
        "description",
        "output_type",
        "maxValues",
      ];
      const childKeys = Object.keys(obj).filter(
        (key) => !tagMetadataKeys.includes(key),
      );

      childKeys.forEach((key) => {
        const nodePath = [...currentPath, key];
        const nodeKey = nodePath.join(".");

        if (currentLevel === targetLevel) {
          // Remove from collapsed set to expand
          newCollapsed.delete(nodeKey);
        }

        // Recurse into children
        processLevel(obj[key], nodePath, currentLevel + 1);
      });
    };

    // Process all root nodes
    Object.entries(tagsToAnalyze).forEach(([rootName, rootValue]) => {
      if (targetLevel === 1) {
        newCollapsed.delete(rootName);
      }
      processLevel(rootValue, [rootName], 2);
    });

    setCollapsedNodes(newCollapsed);
  };

  // Collapse all nodes at a specific level
  const handleCollapseLevel = (targetLevel) => {
    const newCollapsed = new Set(collapsedNodes);
    const tagsToAnalyze = isHierarchical ? reorganizedTags : suggestedTags;

    const processLevel = (obj, currentPath = [], currentLevel = 1) => {
      if (!obj || typeof obj !== "object") return;

      const tagMetadataKeys = [
        "TagAvailableValues",
        "description",
        "output_type",
        "maxValues",
      ];
      const childKeys = Object.keys(obj).filter(
        (key) => !tagMetadataKeys.includes(key),
      );

      childKeys.forEach((key) => {
        const nodePath = [...currentPath, key];
        const nodeKey = nodePath.join(".");

        if (currentLevel === targetLevel && childKeys.length > 0) {
          // Add to collapsed set to collapse
          newCollapsed.add(nodeKey);
        }

        // Recurse into children
        processLevel(obj[key], nodePath, currentLevel + 1);
      });
    };

    // Process all root nodes
    Object.entries(tagsToAnalyze).forEach(([rootName, rootValue]) => {
      const tagMetadataKeys = [
        "TagAvailableValues",
        "description",
        "output_type",
        "maxValues",
      ];
      const childKeys = Object.keys(rootValue || {}).filter(
        (key) => !tagMetadataKeys.includes(key),
      );

      if (targetLevel === 1 && childKeys.length > 0) {
        newCollapsed.add(rootName);
      }
      processLevel(rootValue, [rootName], 2);
    });

    setCollapsedNodes(newCollapsed);
  };

  const handleConfirm = () => {
    // Filter out the tags that are not selected
    const filteredTags = {};

    // Use reorganized tags if hierarchical, otherwise use original suggested tags
    const tagsToFilter = isHierarchical ? reorganizedTags : suggestedTags;

    const filterTagsRecursively = (
      originalTags,
      filteredObj = {},
      path = [],
    ) => {
      if (!originalTags || typeof originalTags !== "object") return filteredObj;

      Object.keys(originalTags).forEach((tagName) => {
        const currentPath = [...path, tagName];
        const tagKey = currentPath.join(".");

        if (selectedTags[tagKey]) {
          // Check if this is a tag (has TagAvailableValues) or a group
          const isTag = originalTags[tagName]?.TagAvailableValues !== undefined;

          if (isTag) {
            // This is a tag, copy the tag metadata first
            const tagMetadataKeys = [
              "TagAvailableValues",
              "description",
              "output_type",
              "maxValues",
            ];

            filteredObj[tagName] = {};

            // Copy tag metadata
            tagMetadataKeys.forEach((key) => {
              if (originalTags[tagName][key] !== undefined) {
                filteredObj[tagName][key] = originalTags[tagName][key];
              }
            });

            // Check if this tag also has children (non-metadata properties)
            const childKeys = Object.keys(originalTags[tagName]).filter(
              (key) => !tagMetadataKeys.includes(key),
            );

            if (childKeys.length > 0) {
              // Recursively filter children
              filterTagsRecursively(
                originalTags[tagName],
                filteredObj[tagName],
                currentPath,
              );
            }
          } else if (typeof originalTags[tagName] === "object") {
            // This is a group/folder, create it and recursively process children
            filteredObj[tagName] = {};
            filterTagsRecursively(
              originalTags[tagName],
              filteredObj[tagName],
              currentPath,
            );

            // If the group has no children after filtering, remove it
            if (Object.keys(filteredObj[tagName]).length === 0) {
              delete filteredObj[tagName];
            }
          }
        } else if (
          typeof originalTags[tagName] === "object" &&
          !originalTags[tagName]?.TagAvailableValues
        ) {
          // This group itself is not selected, but check if any children are selected
          const tempGroup = {};
          filterTagsRecursively(originalTags[tagName], tempGroup, currentPath);

          // If any children were selected, include this group
          if (Object.keys(tempGroup).length > 0) {
            filteredObj[tagName] = tempGroup;
          }
        }
      });

      return filteredObj;
    };
    filterTagsRecursively(tagsToFilter, filteredTags);
    onConfirm(filteredTags, selectedTagNames);
  };

  const getScoreFromNotFoundRate = (tagName) => {
    const rate = tagNotFoundRates[tagName];
    if (rate === undefined || rate === null || rate < 0) return null;
    return Math.round((1 - rate) * 100);
  };

  const getScoreCategory = (score) => {
    if (score === null || score === undefined) return "unscored";
    if (score >= 80) return "high";
    if (score >= 40) return "medium";
    return "low";
  };

  const getScoreColorClasses = (score) => {
    if (score === null || score === undefined) {
      return {
        bg: "bg-gray-100",
        text: "text-gray-600",
        border: "border-gray-200",
      };
    }
    if (score >= 80) {
      return {
        bg: "bg-green-50",
        text: "text-green-700",
        border: "border-green-200",
      };
    }
    if (score >= 40) {
      return {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
      };
    }
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
    };
  };

  // Calculate group score as average of child scores
  const calculateGroupScore = (node) => {
    if (!node || typeof node !== "object") return null;

    let totalScore = 0;
    let scoreCount = 0;

    const collectScores = (obj, path = []) => {
      if (!obj || typeof obj !== "object") return;

      Object.keys(obj).forEach((key) => {
        const currentPath = [...path, key];

        // If this is a tag (has TagAvailableValues), get its score
        if (obj[key]?.TagAvailableValues !== undefined) {
          const score = getScoreFromNotFoundRate(key);
          if (score !== null && score !== undefined) {
            totalScore += score;
            scoreCount++;
          }
        } else if (typeof obj[key] === "object") {
          // Recurse into nested objects
          collectScores(obj[key], currentPath);
        }
      });
    };

    collectScores(node);
    return scoreCount > 0 ? Math.round(totalScore / scoreCount) : null;
  };

  // Handle drag and drop for reorganizing groups
  const handleDragStart = (e, path) => {
    setDraggedItem(path);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetPath) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.join(".") === targetPath.join(".")) {
      setDraggedItem(null);
      return;
    }

    // Move the dragged item to be a sibling of the target
    const newReorganizedTags = JSON.parse(JSON.stringify(reorganizedTags));

    // Get the dragged item
    const getDraggedItem = (obj, path) => {
      let current = obj;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      const item = current[path[path.length - 1]];
      delete current[path[path.length - 1]];
      return item;
    };

    // Place item at target location (same level as target)
    const placeItem = (obj, targetPath, item, draggedName) => {
      let current = obj;
      for (let i = 0; i < targetPath.length - 1; i++) {
        current = current[targetPath[i]];
      }
      current[draggedName] = item;
    };

    const draggedItem_copy = getDraggedItem(newReorganizedTags, draggedItem);
    const draggedName = draggedItem[draggedItem.length - 1];

    // If dropping at root level, place at root
    if (targetPath.length === 1) {
      newReorganizedTags[draggedName] = draggedItem_copy;
    } else {
      // Place as sibling of target
      placeItem(newReorganizedTags, targetPath, draggedItem_copy, draggedName);
    }

    setReorganizedTags(newReorganizedTags);
    setDraggedItem(null);
  };

  const renderFlatTagsList = () => {
    let filteredTags = flatTagsList.filter(
      (tag) =>
        !searchQuery ||
        tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tag.description &&
          tag.description.toLowerCase().includes(searchQuery.toLowerCase())),
    );

    // Apply score filter
    if (scoreFilter !== "all") {
      filteredTags = filteredTags.filter((tag) => {
        const score = getScoreFromNotFoundRate(tag.name);
        const category = getScoreCategory(score);
        return category === scoreFilter;
      });
    }

    // Sort by score (highest to lowest)
    const sortedTags = [...filteredTags].sort((a, b) => {
      const scoreA = getScoreFromNotFoundRate(a.name) || -1;
      const scoreB = getScoreFromNotFoundRate(b.name) || -1;

      // Sort by score first (descending)
      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }

      // Then by path
      const aPathStr = a.path.join(".");
      const bPathStr = b.path.join(".");
      return aPathStr.localeCompare(bPathStr);
    });

    // Render flat view
    const renderFlatView = () => {
      return (
        <div className="space-y-2">
          {sortedTags.map((tag) => {
            const isSelected = selectedTags[tag.key];
            const score = getScoreFromNotFoundRate(tag.name);
            const colorClasses = getScoreColorClasses(score);

            return (
              <div
                key={tag.key}
                onClick={() => handleToggleTag(tag.key, tag.path)}
                className={`rounded-lg shadow-sm border cursor-pointer transform transition-all duration-200 p-4
                  ${
                    isSelected
                      ? "border-primary/30 bg-primary/5 shadow-sm hover:bg-primary/10"
                      : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-gray-100"
                  } 
                  active:scale-[0.99]`}
              >
                {/* Breadcrumb path */}
                {tag.path.length > 1 && (
                  <div className="text-xs text-gray-500 mb-2 flex items-center">
                    {tag.path.slice(0, -1).map((segment, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <ChevronRight size={12} className="mx-1" />
                        )}
                        <span>{segment}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Tag
                      size={16}
                      className={`mr-3 ${isSelected ? "text-primary" : "text-gray-400"}`}
                    />
                    <span
                      className={`text-sm font-medium ${isSelected ? "text-primary-900" : "text-gray-700"}`}
                    >
                      {tag.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {score !== null ? (
                      <div
                        className={`px-2 py-0.5 rounded-full ${colorClasses.bg} ${colorClasses.border} border`}
                      >
                        <span
                          className={`text-xs font-medium ${colorClasses.text}`}
                        >
                          {score}%
                        </span>
                      </div>
                    ) : (
                      <div className="px-2 py-0.5 rounded-full bg-gray-100 border-gray-200 border">
                        <span className="text-xs font-medium text-gray-600">
                          No score
                        </span>
                      </div>
                    )}
                    {isSelected && (
                      <div className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
                        ✓
                      </div>
                    )}
                  </div>
                </div>

                {/* Tag details */}
                {(tag.description ||
                  tag.outputType ||
                  tag.maxValues ||
                  tag.availableValues.length > 0) && (
                  <div className="mt-3 ml-7 space-y-2">
                    {tag.description && (
                      <div className="flex">
                        <FileText
                          size={12}
                          className={`mr-2 mt-0.5 flex-shrink-0 ${isSelected ? "text-primary/70" : "text-gray-400"}`}
                        />
                        <span
                          className={`text-xs ${isSelected ? "text-primary-700" : "text-gray-500"}`}
                        >
                          {tag.description}
                        </span>
                      </div>
                    )}
                    {tag.outputType && (
                      <div className="flex items-center">
                        <Type
                          size={12}
                          className={`mr-2 ${isSelected ? "text-primary/70" : "text-gray-400"}`}
                        />
                        <span
                          className={`text-xs ${isSelected ? "text-primary-700" : "text-gray-500"}`}
                        >
                          Output Type:{" "}
                          <span className="font-medium">
                            {tag.outputType.charAt(0).toUpperCase() +
                              tag.outputType.slice(1)}
                          </span>
                        </span>
                      </div>
                    )}
                    {tag.availableValues.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {tag.availableValues.map((value, idx) => (
                          <span
                            key={idx}
                            className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                              isSelected
                                ? value === "<ANY>" || value === "Open-ended"
                                  ? "bg-primary/10 text-primary"
                                  : "bg-gray-100 text-gray-700 border border-gray-300"
                                : value === "<ANY>" || value === "Open-ended"
                                  ? "bg-gray-50 text-gray-600"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {value === "<ANY>" ? "Open-ended" : value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };
    // Choose view based on mode
    if (viewMode === "flat") {
      return renderFlatView();
    }

    // For hierarchical tree view, render the reorganized structure
    return renderHierarchicalTree();
  };

  // Render hierarchical tree view with compact birds eye view and drag-and-drop
  const renderHierarchicalTree = () => {
    const renderHierarchicalNode = (
      obj,
      name,
      path = [],
      depth = 0,
      isLast = false,
    ) => {
      const currentPath = [...path, name];
      const nodeKey = currentPath.join(".");
      const isCollapsed = collapsedNodes.has(nodeKey);

      // Check if this is a tag (has TagAvailableValues) or a group
      const isTag = obj?.TagAvailableValues !== undefined;

      // For hierarchical structures, a node can be both a tag AND have children
      // Children are any properties that aren't tag metadata
      const tagMetadataKeys = [
        "TagAvailableValues",
        "description",
        "output_type",
        "maxValues",
      ];
      const childKeys =
        typeof obj === "object"
          ? Object.keys(obj).filter((key) => !tagMetadataKeys.includes(key))
          : [];
      const hasChildren = childKeys.length > 0;

      // Calculate scores: individual score for tags, group average for pure groups
      const individualScore = isTag ? getScoreFromNotFoundRate(name) : null;
      const groupScore =
        hasChildren && !isTag ? calculateGroupScore(obj) : null;
      const score = individualScore !== null ? individualScore : groupScore;

      const colorClasses = getScoreColorClasses(score);
      const isSelected = selectedTags[nodeKey];

      const isDragTarget = draggedItem && draggedItem.join(".") !== nodeKey;
      const isDragging = draggedItem && draggedItem.join(".") === nodeKey;

      const toggleCollapse = (e) => {
        e.stopPropagation();
        if (!hasChildren) return;

        const newCollapsed = new Set(collapsedNodes);
        if (newCollapsed.has(nodeKey)) {
          newCollapsed.delete(nodeKey);
        } else {
          newCollapsed.add(nodeKey);
        }
        setCollapsedNodes(newCollapsed);
      };

      return (
        <div key={nodeKey} className="relative">
          <div
            draggable={isHierarchical && hasChildren && !isTag}
            onDragStart={(e) =>
              isHierarchical &&
              hasChildren &&
              !isTag &&
              handleDragStart(e, currentPath)
            }
            onDragOver={isDragTarget ? handleDragOver : undefined}
            onDrop={
              isDragTarget ? (e) => handleDrop(e, currentPath) : undefined
            }
            onClick={
              isTag
                ? () => handleToggleTag(nodeKey, currentPath)
                : toggleCollapse
            }
            className={`
              flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all mb-1
              ${isDragging ? "opacity-50" : ""}
              ${isDragTarget ? "bg-blue-50 border-2 border-dashed border-blue-300" : ""}
              ${depth === 0 ? "border border-gray-200 bg-white shadow-sm" : ""}
              ${depth === 1 ? "bg-gray-50" : ""}
              ${depth >= 2 ? "bg-gray-100" : ""}
              ${isSelected ? "ring-2 ring-primary/30" : ""}
              hover:shadow-sm
            `}
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
          >
            {/* Expand/Collapse button for groups */}
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the main click handler
                  toggleCollapse(e);
                }}
                className="flex-shrink-0 w-5 h-5 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
              >
                {isCollapsed ? (
                  <ChevronRight size={12} className="text-gray-600" />
                ) : (
                  <ChevronDown size={12} className="text-gray-600" />
                )}
              </button>
            )}

            {/* Icon */}
            <div className="flex-shrink-0">
              {hasChildren ? (
                isCollapsed ? (
                  <Folder size={16} className="text-gray-500" />
                ) : (
                  <FolderOpen size={16} className="text-gray-500" />
                )
              ) : (
                <Tag
                  size={16}
                  className={isSelected ? "text-primary" : "text-gray-400"}
                />
              )}
            </div>

            {/* Name */}
            <span
              className={`font-medium flex-1 truncate ${
                isSelected
                  ? "text-primary-900"
                  : hasChildren
                    ? "text-gray-800"
                    : "text-gray-700"
              }`}
            >
              {name}
            </span>

            {/* Compact metadata for birds eye view */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Children count for groups */}
              {hasChildren && (
                <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                  {childKeys.length}
                </span>
              )}

              {/* Score badge */}
              {score !== null && (
                <div
                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${colorClasses.bg} ${colorClasses.text}`}
                >
                  {score}%{groupScore ? "*" : ""}
                </div>
              )}

              {/* Level indicator */}
              <span className="text-xs text-gray-400 bg-gray-100 px-1 py-0.5 rounded">
                L{depth + 1}
              </span>

              {/* Selection indicator */}
              {isSelected && (
                <div className="w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}

              {/* Drag handle for groups (not individual tags) */}
              {isHierarchical && hasChildren && !isTag && (
                <div className="w-4 h-4 grid grid-cols-2 gap-0.5 opacity-40 hover:opacity-100">
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                </div>
              )}
            </div>
          </div>

          {/* Render children if not collapsed */}
          {hasChildren && !isCollapsed && (
            <div className="space-y-0.5">
              {childKeys.map((childName, index, array) =>
                renderHierarchicalNode(
                  obj[childName],
                  childName,
                  currentPath,
                  depth + 1,
                  index === array.length - 1,
                ),
              )}
            </div>
          )}
        </div>
      );
    };

    const tagsToRender = isHierarchical ? reorganizedTags : suggestedTags;

    if (!Object.keys(tagsToRender).length) {
      return (
        <div className="p-8 text-center text-gray-500">
          <Info size={24} className="mx-auto mb-2 text-gray-400" />
          <p className="font-medium">No tags available for selection</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {/* Info banner for hierarchical view */}
        {isHierarchical && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2 text-sm text-blue-800">
              <Info size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <p>
                  <strong>Birds Eye View:</strong> Compact hierarchical
                  structure with drag-and-drop organization.
                </p>
                <p className="text-xs mt-1">
                  • Scores with (*) are group averages • Drag folders to
                  reorganize • Use level controls to expand/collapse specific
                  depths
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Render the tree */}
        <div className="space-y-1">
          {Object.entries(tagsToRender).map(
            ([rootName, rootValue], index, array) =>
              renderHierarchicalNode(
                rootValue,
                rootName,
                [],
                0,
                index === array.length - 1,
              ),
          )}
        </div>
      </div>
    );
  };
  const tagObjectsSelected = Object.entries(selectedTags).filter(
    ([key, value]) =>
      !key.includes("TagAvailableValues") &&
      // every other item in the key string is a value, not a tag name
      key.split(".").length % 2 !== 0 &&
      value,
  );
  const selectedCount = Object.values(tagObjectsSelected).length;
  const totalCount = Object.keys(selectedTags).filter(
    (key) =>
      !key.includes("TagAvailableValues") && key.split(".").length % 2 !== 0,
  ).length;

  // Calculate filtered count
  const getFilteredCount = () => {
    if (scoreFilter === "all") return totalCount;
    return flatTagsList.filter((tag) => {
      const score = getScoreFromNotFoundRate(tag.name);
      const category = getScoreCategory(score);
      return category === scoreFilter;
    }).length;
  };

  const filteredCount = getFilteredCount();

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95vw",
          maxWidth: "800px",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Generated Schemas
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-gray-500">
                  Choose which tags to include in your graph
                </p>
                <span className="text-sm font-medium text-gray-700">
                  ({flatTagsList.length} total tags)
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          {Object.keys(tagNotFoundRates).length > 0 && (
            <div className="mt-3 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Info size={14} className="text-gray-400" />
                <span>Score indicates data coverage (click to filter):</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() =>
                    setScoreFilter(scoreFilter === "high" ? "all" : "high")
                  }
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                    scoreFilter === "high"
                      ? "bg-green-100 text-green-800 border-2 border-green-300"
                      : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                  }`}
                >
                  80-100% High
                </button>
                <button
                  onClick={() =>
                    setScoreFilter(scoreFilter === "medium" ? "all" : "medium")
                  }
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                    scoreFilter === "medium"
                      ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                      : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                  }`}
                >
                  40-79% Medium
                </button>
                <button
                  onClick={() =>
                    setScoreFilter(scoreFilter === "low" ? "all" : "low")
                  }
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                    scoreFilter === "low"
                      ? "bg-red-100 text-red-800 border-2 border-red-300"
                      : "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                  }`}
                >
                  0-39% Low
                </button>
                <button
                  onClick={() =>
                    setScoreFilter(
                      scoreFilter === "unscored" ? "all" : "unscored",
                    )
                  }
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                    scoreFilter === "unscored"
                      ? "bg-gray-200 text-gray-800 border-2 border-gray-300"
                      : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-150"
                  }`}
                >
                  No Score
                </button>
                {scoreFilter !== "all" && (
                  <button
                    onClick={() => setScoreFilter("all")}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-150 text-xs font-medium"
                  >
                    Show All
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Search & Controls */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-col space-y-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-3 top-3.5 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search tags or descriptions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                />
              </div>
              {/* View mode toggle - only show for hierarchical */}
              {isHierarchical && (
                <div className="flex bg-white border border-gray-200 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("tree")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                      viewMode === "tree"
                        ? "bg-primary text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Tree View
                  </button>
                  <button
                    onClick={() => setViewMode("flat")}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                      viewMode === "flat"
                        ? "bg-primary text-white"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Flat View
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {scoreFilter === "all"
                    ? "Select All"
                    : scoreFilter === "unscored"
                      ? "Select All Unscored"
                      : `Select All ${scoreFilter.charAt(0).toUpperCase() + scoreFilter.slice(1)}`}
                </button>
                <button
                  onClick={handleSelectNone}
                  className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {scoreFilter === "all"
                    ? "Clear All"
                    : scoreFilter === "unscored"
                      ? "Clear Unscored"
                      : `Clear ${scoreFilter.charAt(0).toUpperCase() + scoreFilter.slice(1)}`}
                </button>
                {isHierarchical && viewMode === "tree" && (
                  <>
                    <div className="w-px bg-gray-200 mx-1" />
                    <button
                      onClick={handleExpandAll}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      <ChevronDown size={14} />
                      Expand All
                    </button>
                    <button
                      onClick={handleCollapseAll}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      <ChevronRight size={14} />
                      Collapse All
                    </button>
                  </>
                )}
                {isHierarchical && (
                  <>
                    <div className="w-px bg-gray-200 mx-1" />
                    <button
                      onClick={handleReset}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Reset
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Level-specific controls for hierarchical tree view */}
            {isHierarchical &&
              viewMode === "tree" &&
              (() => {
                const maxDepth = getMaxDepth();
                return maxDepth > 1 ? (
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                    <span className="text-xs font-medium text-gray-600">
                      By Level:
                    </span>
                    <div className="flex items-center gap-1">
                      {[...Array(maxDepth)].map((_, index) => {
                        const level = index + 1;
                        return (
                          <div
                            key={level}
                            className="flex items-center bg-white border border-gray-200 rounded-md overflow-hidden"
                          >
                            <button
                              onClick={() => handleExpandLevel(level)}
                              className="px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1 border-r border-gray-200"
                              title={`Expand Level ${level}`}
                            >
                              <ChevronDown size={10} />L{level}
                            </button>
                            <button
                              onClick={() => handleCollapseLevel(level)}
                              className="px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              title={`Collapse Level ${level}`}
                            >
                              <ChevronRight size={10} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null;
              })()}
          </div>
        </div>

        {/* Tag List */}
        <div className="flex-1 overflow-auto p-5 bg-gray-50">
          {scoreFilter !== "all" && (
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              Showing{" "}
              {(() => {
                const filteredCount = flatTagsList.filter((tag) => {
                  const score = getScoreFromNotFoundRate(tag.name);
                  const category = getScoreCategory(score);
                  return category === scoreFilter;
                }).length;
                return filteredCount;
              })()}{" "}
              {scoreFilter === "unscored"
                ? "unscored"
                : `${scoreFilter} coverage`}{" "}
              tags out of {flatTagsList.length} total
            </div>
          )}
          {flatTagsList.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Info size={24} className="mx-auto mb-2 text-gray-400" />
              No tags available for selection
            </div>
          ) : (
            renderFlatTagsList()
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedCount} selected
            {scoreFilter !== "all" &&
              ` of ${filteredCount} ${scoreFilter === "unscored" ? "unscored" : `${scoreFilter} coverage`} tags`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className={`px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 ${
                selectedCount === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Apply {selectedCount > 0 && `${selectedCount}`} Tag
              {selectedCount !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
